import store from '../store/store'

class TokenRefreshManager {
	constructor() {
		this.refreshInProgress = false
		this.refreshPromise = null
	}

	shouldRefreshToken(user) {
		if (!user?.kc_token) return false

		if (!this.isRefreshTokenValid(user)) {
			console.log('Refresh token is expired or expiring soon, not attempting refresh')
			return false
		}

		const decoded = this.decodeToken(user.kc_token)
		if (!decoded?.exp) return false

		const expiresAt = decoded.exp * 1000
		const now = Date.now()
		const timeUntilExpiry = expiresAt - now

		return timeUntilExpiry < (2 * 60 * 1000)
	}

	decodeToken(token) {
		try {
			return JSON.parse(atob(token.split('.')[1]))
		}
		catch (e) {
			console.error('Failed to decode token:', e)
			return null
		}
	}

	isRefreshTokenValid(user) {
		if (!user?.kc_refresh_token) return false

		const decoded = this.decodeToken(user.kc_refresh_token)
		if (!decoded?.exp) return false

		const expiresAt = decoded.exp * 1000
		const now = Date.now()

		return (expiresAt - now) > 30000
	}

	getRefreshTokenExpiresInSeconds(user) {
		if (!user?.kc_refresh_token) return 0

		const decoded = this.decodeToken(user.kc_refresh_token)
		if (!decoded?.exp) return 0

		const expiresAt = decoded.exp * 1000
		const now = Date.now()

		return Math.max(0, Math.floor((expiresAt - now) / 1000))
	}

	async refreshToken() {
		if (this.refreshInProgress) {
			return this.refreshPromise
		}

		const user = store.state.loggedInUser
		if (!user?.kc_refresh_token) {
			throw new Error('No refresh token available')
		}

		if (!this.isRefreshTokenValid(user)) {
			console.log('Refresh token expired, cannot refresh')
			throw new Error('Refresh token expired')
		}

		this.refreshInProgress = true

		this.refreshPromise = fetch('/api/refresh-token', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				refresh_token: user.kc_refresh_token
			})
		})
		.then(async response => {
			if (!response.ok) {
				throw new Error('Token refresh failed')
			}

			const updatedUser = await response.json()
			await store.commit('setLoggedInUser', updatedUser)

			this.scheduleTokenRefresh(updatedUser)

			console.log('Token refreshed successfully')
			return updatedUser
		})
		.catch(error => {
			console.error('Token refresh error:', error)
			store.commit('clearRefreshTimer')

			const currentUser = store.state.loggedInUser
			if (!this.isRefreshTokenValid(currentUser)) {
				console.log('Refresh token expired after failed refresh attempt')
			}

			throw error
		})
		.finally(() => {
			this.refreshInProgress = false
			this.refreshPromise = null
		})

		return this.refreshPromise
	}

	scheduleTokenRefresh(user) {
		store.commit('clearRefreshTimer')

		if (!user?.kc_token) return

		if (!this.isRefreshTokenValid(user)) {
			console.log('Refresh token expired, not scheduling refresh')
			return
		}

		const accessDecoded = this.decodeToken(user.kc_token)
		const refreshDecoded = this.decodeToken(user.kc_refresh_token)

		if (!accessDecoded?.exp || !refreshDecoded?.exp) return

		const accessExpiresAt = accessDecoded.exp * 1000
		const refreshExpiresAt = refreshDecoded.exp * 1000
		const now = Date.now()

		const timeUntilAccessExpiry = accessExpiresAt - now

		const timeDifference = refreshExpiresAt - accessExpiresAt
		if (timeDifference < (2 * 60 * 1000)) {
			console.log('This is the last token (refresh token expires soon), not scheduling refresh')
			return
		}

		const refreshTime = Math.max(0, timeUntilAccessExpiry - (2 * 60 * 1000))

		if (refreshTime < 5000) {
			console.log('Refresh time too short, not scheduling (likely last token)')
			return
		}

		console.log(`Scheduling token refresh in ${Math.floor(refreshTime / 1000)} seconds`)

		const timer = setTimeout(() => {
			this.refreshToken().catch(err => {
				console.error('Scheduled token refresh failed:', err)
			})
		}, refreshTime)

		store.state.refreshTokenTimer = timer
	}

	initialize() {
		const user = store.state.loggedInUser
		if (user?.kc_token) {
			this.scheduleTokenRefresh(user)
		}
	}
}

export const tokenRefreshManager = new TokenRefreshManager()
export default tokenRefreshManager